import { __ } from '@wordpress/i18n';

export const fieldTypes = [
  { value: 'text', label: __('Text Input', 'havenlytics'), icon: 'font', isGroup: false },
  { value: 'textarea', label: __('Text Area', 'havenlytics'), icon: 'align-left', isGroup: false },
  { value: 'number', label: __('Number', 'havenlytics'), icon: 'hashtag', isGroup: false },
  { value: 'checkbox', label: __('Checkbox List Feature', 'havenlytics'), icon: 'check-square', isGroup: false },
  { value: 'image', label: __('Image Upload', 'havenlytics'), icon: 'image', isGroup: false },
  { value: 'gallery', label: __('Gallery (2 Fields)', 'havenlytics'), icon: 'images', isGroup: true, groupType: 'gallery' },
  { value: 'video', label: __('Video (3 Fields)', 'havenlytics'), icon: 'video', isGroup: true, groupType: 'video' },
  { value: 'map', label: __('Map Location (4 Fields)', 'havenlytics'), icon: 'map-marker-alt', isGroup: true, groupType: 'map' },
  { value: 'property_docs', label: __('Property Documents', 'havenlytics'), icon: 'file-pdf', isGroup: true, groupType: 'property_docs' }
];

// Group field definitions
export const groupDefinitions = {
  video: {
    name: __('Video Information', 'havenlytics'),
    icon: 'video',
    fieldCount: 3,
    fields: [
      {
        id: 'title',
        type: 'text',
        label: __('Video Title', 'havenlytics'),
        placeholder: __('Enter video title', 'havenlytics'),
        required: false,
        metaKey: 'title'
      },
      {
        id: 'url',
        type: 'text',
        label: __('Video URL', 'havenlytics'),
        placeholder: __('Enter video URL', 'havenlytics'),
        required: false,
        metaKey: 'url'
      },
      {
        id: 'thumbnail',
        type: 'image',
        label: __('Video Thumbnail', 'havenlytics'),
        placeholder: __('Upload video thumbnail', 'havenlytics'),
        required: false,
        metaKey: 'thumbnail'
      }
    ]
  },
  gallery: {
    name: __('Property Gallery', 'havenlytics'),
    icon: 'images',
    fieldCount: 2,
    fields: [
      {
        id: 'title',
        type: 'text',
        label: __('Gallery Title', 'havenlytics'),
        placeholder: __('Enter gallery title', 'havenlytics'),
        required: false,
        metaKey: 'title'
      },
      {
        id: 'images',
        type: 'gallery',
        label: __('Gallery Images', 'havenlytics'),
        placeholder: __('Upload gallery images', 'havenlytics'),
        required: false,
        metaKey: 'images'
      }
    ]
  },
  map: {
    name: __('Map Location', 'havenlytics'),
    icon: 'map-marker-alt',
    fieldCount: 4,
    fields: [
      {
        id: 'address',
        type: 'text',
        label: __('Property Map Address', 'havenlytics'),
        placeholder: __('Enter property map address', 'havenlytics'),
        required: true,
        metaKey: 'address'
      },
      {
        id: 'latitude',
        type: 'text',
        label: __('Property Latitude', 'havenlytics'),
        placeholder: __('Enter property latitude', 'havenlytics'),
        required: false,
        metaKey: 'latitude',
        description: __('Get the property latitude by clicking on Google Maps or visiting https://www.maps.ie/coordinates.html', 'havenlytics')
      },
      {
        id: 'longitude',
        type: 'text',
        label: __('Property Longitude', 'havenlytics'),
        placeholder: __('Enter property longitude', 'havenlytics'),
        required: false,
        metaKey: 'longitude',
        description: __('Get the property longitude by clicking on Google Maps or visiting https://www.maps.ie/coordinates.html', 'havenlytics')
      },
      {
        id: 'preview',
        type: 'map',
        label: __('Property Location Map', 'havenlytics'),
        placeholder: __('Select location on map', 'havenlytics'),
        required: false,
        metaKey: 'preview'
      }
    ]
  },
  property_docs: {
    name: __('Property Documents', 'havenlytics'),
    icon: 'file-pdf',
    fieldCount: 3,
    fields: [
      {
        id: 'icon',
        type: 'text',
        label: __('Document Icon', 'havenlytics'),
        placeholder: __('e.g., file-pdf, print, video', 'havenlytics'),
        required: false,
        metaKey: 'icon',
        description: __('Enter FontAwesome icon name (without fa- prefix)', 'havenlytics')
      },
      {
        id: 'label',
        type: 'text',
        label: __('Document Label', 'havenlytics'),
        placeholder: __('e.g., Floor Plan, Brochure, EPC', 'havenlytics'),
        required: true,
        metaKey: 'label'
      },
      {
        id: 'url',
        type: 'text',
        label: __('Document URL', 'havenlytics'),
        placeholder: __('Enter document URL or file path', 'havenlytics'),
        required: true,
        metaKey: 'url'
      }
    ]
  }
};

/**
 * Check if field belongs to a group
 */
export const isFieldInGroup = (field) => {
  return field && field.group_id && field.group_type;
};

/**
 * Get group information for a field
 */
export const getFieldGroupInfo = (field) => {
  if (!isFieldInGroup(field)) return null;
  
  return {
    id: field.group_id,
    type: field.group_type,
    name: field.group_name || groupDefinitions[field.group_type]?.name || field.group_type,
    position: field.group_position || 0,
    total: field.group_total || 0,
    icon: groupDefinitions[field.group_type]?.icon || 'folder'
  };
};

/**
 * Check if all fields in a group are present
 */
export const isCompleteGroup = (fields, groupId) => {
  const groupFields = fields.filter(f => f.group_id === groupId);
  const groupType = groupFields[0]?.group_type;
  
  if (!groupType) return false;
  
  const expectedCount = groupDefinitions[groupType]?.fieldCount || 0;
  return groupFields.length === expectedCount;
};

/**
 * Organize fields by group
 */
export const organizeFieldsByGroup = (fields) => {
  const organized = {
    groups: {},
    standalone: []
  };
  
  fields.forEach(field => {
    if (isFieldInGroup(field)) {
      const groupId = field.group_id;
      
      if (!organized.groups[groupId]) {
        organized.groups[groupId] = {
          id: groupId,
          type: field.group_type,
          name: field.group_name || groupDefinitions[field.group_type]?.name || field.group_type,
          icon: groupDefinitions[field.group_type]?.icon || 'folder',
          fields: [],
          collapsed: false,
          show_in_sidebar: field.show_in_sidebar !== undefined ? field.show_in_sidebar : true
        };
      }
      
      organized.groups[groupId].fields.push(field);
      
      // Sort fields by group position
      organized.groups[groupId].fields.sort((a, b) => {
        return (a.group_position || 0) - (b.group_position || 0);
      });
    } else {
      organized.standalone.push(field);
    }
  });
  
  return organized;
};

/**
 * Generate group ID
 */
export const generateGroupId = (groupType) => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 10);
  return `grp_${groupType}_${timestamp}_${random}`;
};

/**
 * Generate field name with prefix
 */
export const generateFieldName = (prefix, fieldId) => {
  return `${prefix}${fieldId}`;
};

export const getDefaultLabel = (fieldType) => {
  const labelMap = {
    text: __('Text Field', 'havenlytics'),
    textarea: __('Text Area', 'havenlytics'),
    number: __('Number', 'havenlytics'),
    checkbox: __('Checkbox', 'havenlytics'),
    image: __('Image Upload', 'havenlytics'),
    gallery: __('Gallery', 'havenlytics'),
    video: __('Video', 'havenlytics'),
    map: __('Map Location', 'havenlytics'),
    property_docs: __('Property Documents', 'havenlytics')
  };
  
  return labelMap[fieldType] || __('New Field', 'havenlytics');
};

export const getDefaultPlaceholder = (fieldType) => {
  const placeholderMap = {
    text: __('Enter text here', 'havenlytics'),
    textarea: __('Enter detailed information here', 'havenlytics'),
    number: __('Enter number here', 'havenlytics'),
    checkbox: '',
    image: __('Upload image', 'havenlytics'),
    gallery: __('Upload multiple images', 'havenlytics'),
    video: __('Upload video', 'havenlytics'),
    map: __('Select location on map', 'havenlytics'),
    property_docs: __('Add property documents', 'havenlytics')
  };
  
  return placeholderMap[fieldType] || __('Enter value here', 'havenlytics');
};

export const getIconForType = (type) => {
  const iconMap = {
    text: 'font',
    textarea: 'align-left',
    number: 'hashtag',
    checkbox: 'check-square',
    image: 'image',
    gallery: 'images',
    video: 'video',
    map: 'map-marker-alt',
    property_docs: 'file-pdf'
  };
  
  return iconMap[type] || 'font';
};

export const getActiveItem = (activeDragId, tabs) => {
  if (!activeDragId) return null;
  
  const activeTab = tabs.find(tab => tab.id === activeDragId);
  if (activeTab) {
    return { type: 'tab', data: activeTab };
  }
  
  for (const tab of tabs) {
    const activeField = tab.fields.find(field => field.id === activeDragId);
    if (activeField) {
      return { type: 'field', data: activeField };
    }
  }
  
  if (activeDragId.includes('text') || 
      activeDragId.includes('textarea') || 
      activeDragId.includes('number') || 
      activeDragId.includes('checkbox') || 
      activeDragId.includes('image') || 
      activeDragId.includes('gallery') || 
      activeDragId.includes('video') || 
      activeDragId.includes('map') ||
      activeDragId.includes('property_docs')) {
    
    const fieldType = activeDragId.split('_')[0];
    return { 
      type: 'component', 
      data: { 
        type: fieldType, 
        label: getDefaultLabel(fieldType) 
      } 
    };
  }
  
  return null;
};